import { Controller } from '@hotwired/stimulus';

// Preset recipes — the same five URL recipes listed further down the page.
const PRESETS = {
  sales: { orientation: 'portrait', size: 'a4', scale: '2', fitw: true, gid: '123456789', printtitle: true, pagenum: true, sheetnames: false },
  client: { orientation: 'portrait', size: 'a4', scale: '1', fitw: false, top_margin: '0.5', bottom_margin: '0.5', left_margin: '0.5', right_margin: '0.5', gridlines: false },
  warehouse: { orientation: 'landscape', size: 'a4', scale: '2', fitw: true, fzr: true, fzc: true, gridlines: true },
  audit: { orientation: 'portrait', size: 'a4', r1: '6', c1: '1', r2: '18', c2: '7', printnotes: false },
  minimal: { orientation: 'portrait', size: 'a4', sheetnames: false, printtitle: false, pagenum: false, gridlines: false }
};

const DEFAULTS = {
  orientation: 'portrait', size: 'letter', scale: '',
  fitw: false, gridlines: false, pagenum: false, printtitle: false,
  sheetnames: false, printnotes: false, fzr: false, fzc: false,
  gid: '', r1: '', c1: '', r2: '', c2: '',
  top_margin: '0.5', bottom_margin: '0.5', left_margin: '0.5', right_margin: '0.5'
};

// Google's export honours these names. Its numeric size codes don't line up
// with the labels (size=1 is 7.5x10, not Tabloid; 3-5 and 9-10 are A0-A2/A6-A7).
const SIZE_LABELS = {
  letter: 'Letter', tabloid: 'Tabloid', legal: 'Legal', statement: 'Statement', executive: 'Executive',
  folio: 'Folio', a3: 'A3', a4: 'A4', a5: 'A5', b4: 'B4', b5: 'B5'
};

// Older builder links carry numeric sizes (e.g. /pdf?size=7). Map each to the
// paper its button was labelled with.
const LEGACY_SIZES = {
  0: 'letter', 1: 'tabloid', 2: 'legal', 3: 'statement', 4: 'executive', 5: 'folio',
  6: 'a3', 7: 'a4', 8: 'a5', 9: 'b4', 10: 'b5'
};

const RANGE_KEYS = ['gid', 'r1', 'c1', 'r2', 'c2'];
const MARGIN_KEYS = ['top_margin', 'bottom_margin', 'left_margin', 'right_margin'];
const CHECKBOX_KEYS = ['fitw', 'gridlines', 'printtitle', 'sheetnames', 'printnotes', 'fzr', 'fzc'];

// A stand-in id so the live link reads like a real export URL before a sheet
// URL has been pasted in.
const SAMPLE_ID = 'YOUR_SHEET_ID';
const PX_PER_INCH = 26;

export default class extends Controller {
  static targets = [
    'input', 'output', 'outBox',
    'rangeFields', 'rangeCaret', 'rangeToggle',
    'marginFields', 'marginsCaret', 'marginsToggle',
    'previewBody', 'previewCaret', 'previewToggle',
    'paper', 'paperInner', 'paperLabel', 'paperLabelShort', 'legend',
    'markContent', 'markCol', 'markRow', 'markTitle', 'markSheet', 'markNotes', 'markPagenum'
  ];

  connect() {
    this.prefillFromQueryParams();
    this.render();
  }

  // ---------------------------------------------------------------- state

  field(id) {
    return document.getElementById(id);
  }

  state() {
    const text = (id) => this.field(id)?.value?.trim() || '';
    const checked = (id) => this.field(id)?.checked || false;

    const state = {
      orientation: document.querySelector('input[name="orientation"]:checked')?.value || 'portrait',
      size: document.querySelector('input[name="size"]:checked')?.value || 'letter',
      scale: document.querySelector('input[name="scale"]:checked')?.value || ''
    };

    CHECKBOX_KEYS.forEach((key) => { state[key] = checked(key); });
    state.pagenum = checked('pagenum');
    [...RANGE_KEYS, ...MARGIN_KEYS].forEach((key) => { state[key] = text(key); });

    return state;
  }

  buildUrl(state, baseUrl) {
    const params = new URLSearchParams({
      format: 'pdf',
      portrait: state.orientation === 'portrait' ? 'true' : 'false',
      gridlines: String(!!state.gridlines),
      printtitle: String(!!state.printtitle),
      fitw: String(!!state.fitw),
      fzr: String(!!state.fzr),
      sheetnames: String(!!state.sheetnames),
      size: state.size || 'letter'
    });
    // Leaving pagenum off prints no page numbers.
    if (state.pagenum) params.set('pagenum', 'CENTER');

    ['r1', 'c1', 'r2', 'c2', ...MARGIN_KEYS, 'gid', 'scale'].forEach((key) => {
      if (state[key]) params.set(key, state[key]);
    });
    params.set('fzc', String(!!state.fzc));
    params.set('printnotes', String(!!state.printnotes));

    return `${baseUrl}/export?${params.toString()}`;
  }

  currentUrl() {
    const docInfo = this.extractDocInfo(this.inputTarget.value);

    if (docInfo && docInfo.type !== 'spreadsheets') {
      return { url: `https://docs.google.com/${docInfo.type}/d/${docInfo.id}/export?format=pdf`, valid: true };
    }

    const id = docInfo?.id || SAMPLE_ID;
    return {
      url: this.buildUrl(this.state(), `https://docs.google.com/spreadsheets/d/${id}`),
      valid: Boolean(docInfo)
    };
  }

  // ---------------------------------------------------------------- render

  render() {
    const { url, valid } = this.currentUrl();

    // Nothing to show until a real sheet URL is in the box.
    this.outBoxTarget.hidden = !valid;
    this.lastOutput = valid ? url : null;
    this.outputTarget.textContent = valid ? url : '';
    this.renderPreview();
  }

  renderPreview() {
    if (!this.hasPaperTarget) return;

    const state = this.state();
    const portrait = state.orientation !== 'landscape';
    const inches = (key) => parseFloat(state[key] || 0) || 0;
    const show = (target, visible) => { target.style.display = visible ? 'block' : 'none'; };

    this.paperTarget.style.width = portrait ? '230px' : '320px';
    this.paperTarget.style.height = portrait ? '298px' : '214px';

    const inner = this.paperInnerTarget.style;
    inner.top = `${inches('top_margin') * PX_PER_INCH}px`;
    inner.bottom = `${inches('bottom_margin') * PX_PER_INCH}px`;
    inner.left = `${inches('left_margin') * PX_PER_INCH}px`;
    inner.right = `${inches('right_margin') * PX_PER_INCH}px`;
    this.paperInnerTarget.dataset.gridlines = String(!!state.gridlines);

    this.markContentTarget.style.width = state.fitw ? '100%' : '72%';
    this.markContentTarget.style.borderRight = state.fitw ? '0' : '1px dashed #cbd5e1';

    const headerOffset = state.printtitle || state.sheetnames ? '46px' : '28px';
    this.markRowTarget.style.top = headerOffset;
    this.markColTarget.style.top = headerOffset;
    this.markSheetTarget.style.top = state.printtitle ? '25px' : '8px';

    show(this.markTitleTarget, state.printtitle);
    show(this.markSheetTarget, state.sheetnames);
    show(this.markRowTarget, state.fzr);
    show(this.markColTarget, state.fzc);
    show(this.markPagenumTarget, state.pagenum);
    show(this.markNotesTarget, state.printnotes);

    const label = `${portrait ? 'Portrait' : 'Landscape'} · ${SIZE_LABELS[state.size] || 'Letter'} · ` +
      `margins ${state.top_margin || 0}/${state.right_margin || 0}/${state.bottom_margin || 0}/${state.left_margin || 0}"`;
    this.paperLabelTarget.textContent = label;
    if (this.hasPaperLabelShortTarget) this.paperLabelShortTarget.textContent = label;

    this.legendTarget.textContent = [
      state.fzr || state.fzc ? 'green bands repeat on every page' : null,
      state.printnotes ? 'amber corner marks cell notes' : null
    ].filter(Boolean).join(' · ');
  }

  // ------------------------------------------------------------ disclosures

  toggleRange() {
    this.setDisclosure(this.rangeFieldsTarget, this.rangeToggleTarget, this.rangeCaretTarget, this.rangeFieldsTarget.hidden);
  }

  toggleMargins() {
    this.setDisclosure(this.marginFieldsTarget, this.marginsToggleTarget, this.marginsCaretTarget, this.marginFieldsTarget.hidden);
  }

  togglePreview() {
    const open = this.previewBodyTarget.dataset.collapsed === 'true';
    this.previewBodyTarget.dataset.collapsed = open ? 'false' : 'true';
    this.previewToggleTarget.setAttribute('aria-expanded', String(open));
    this.previewCaretTarget.textContent = open ? '–' : '+';
  }

  setDisclosure(fields, toggle, caret, open) {
    fields.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    caret.textContent = open ? '–' : '+';
  }

  expandRange() {
    this.setDisclosure(this.rangeFieldsTarget, this.rangeToggleTarget, this.rangeCaretTarget, true);
  }

  expandMargins() {
    this.setDisclosure(this.marginFieldsTarget, this.marginsToggleTarget, this.marginsCaretTarget, true);
  }

  // ---------------------------------------------------------------- presets

  applyPreset(event) {
    const preset = PRESETS[event.currentTarget.dataset.preset];
    if (!preset) return;

    const values = { ...DEFAULTS, ...preset };
    this.writeState(values);

    // Never let a collapsed section hide a value the link already carries.
    if (RANGE_KEYS.some((key) => key in preset)) this.expandRange();
    if (MARGIN_KEYS.some((key) => key in preset)) this.expandMargins();

    this.render();
  }

  writeState(values) {
    const orientation = document.querySelector(`input[name="orientation"][value="${values.orientation}"]`);
    if (orientation) orientation.checked = true;

    const size = document.querySelector(`input[name="size"][value="${values.size}"]`);
    if (size) size.checked = true;

    const scale = document.querySelector(`input[name="scale"][value="${values.scale || ''}"]`);
    if (scale) scale.checked = true;

    ['pagenum', ...CHECKBOX_KEYS].forEach((key) => {
      const element = this.field(key);
      if (element) element.checked = !!values[key];
    });

    [...RANGE_KEYS, ...MARGIN_KEYS].forEach((key) => {
      const element = this.field(key);
      if (element) element.value = values[key] ?? '';
    });
  }

  // ------------------------------------------------------------------ copy

  submit(event) {
    event.preventDefault();

    const { url, valid } = this.currentUrl();
    if (!valid) {
      alert('Please enter a valid Google Docs URL');
      return;
    }

    this.lastOutput = url;
    navigator.clipboard.writeText(url);
    this.flashCopied();
  }

  // Click the result box to copy it again.
  copy() {
    if (!this.lastOutput) return;

    navigator.clipboard.writeText(this.lastOutput);
    this.flashCopied();
  }

  flashCopied() {
    const previous = this.outputTarget.textContent;
    this.outputTarget.textContent = 'Copied to clipboard!';

    window.clearTimeout(this.flashTimeoutId);
    this.flashTimeoutId = window.setTimeout(() => {
      this.outputTarget.textContent = previous;
    }, 1200);
  }

  extractDocInfo(url) {
    if (!url) return null;

    // Match Google Docs URL patterns and extract type and ID
    const patterns = {
      'spreadsheets': /\/spreadsheets\/d\/([-\w]{25,})/,
      'document': /\/document\/d\/([-\w]{25,})/,
      'presentation': /\/presentation\/d\/([-\w]{25,})/,
      'drawings': /\/drawings\/d\/([-\w]{25,})/,
      'forms': /\/forms\/d\/([-\w]{25,})/
    };

    for (const [type, pattern] of Object.entries(patterns)) {
      const match = url.match(pattern);
      if (match) {
        return { type: type, id: match[1] };
      }
    }

    return null;
  }

  prefillFromQueryParams() {
    const params = new URLSearchParams(window.location.search);
    if (![...params.keys()].length) return;

    let touchesRange = false;
    let touchesMargins = false;

    const setValue = (id) => {
      const value = params.get(id);
      if (value === null) return;
      const element = this.field(id);
      if (!element) return;
      element.value = value;
      if (RANGE_KEYS.includes(id)) touchesRange = true;
      if (MARGIN_KEYS.includes(id)) touchesMargins = true;
    };

    const setRadio = (name, value) => {
      const element = document.querySelector(`input[name="${name}"][value="${value}"]`);
      if (element) element.checked = true;
    };

    const orientation = params.get('orientation');
    if (orientation) setRadio('orientation', orientation);

    const size = params.get('size');
    if (size !== null) setRadio('size', LEGACY_SIZES[size] || size.toLowerCase());

    const scale = params.get('scale');
    if (scale !== null) setRadio('scale', scale);

    const pagenum = params.get('pagenum');
    if (pagenum !== null) {
      const pagenumCheckbox = this.field('pagenum');
      if (pagenumCheckbox) pagenumCheckbox.checked = !['UNDEFINED', 'FALSE', ''].includes(pagenum.toUpperCase());
    }

    [...RANGE_KEYS, ...MARGIN_KEYS].forEach(setValue);

    CHECKBOX_KEYS.forEach((id) => {
      const value = params.get(id);
      if (value === null) return;
      const element = this.field(id);
      if (!element) return;
      element.checked = value.toLowerCase() === 'true';
    });

    if (touchesRange) this.expandRange();
    if (touchesMargins) this.expandMargins();
  }
}
